import React, { useMemo, useState } from "react";
import { Modal } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";
import { createAddendum, updateAddendum } from "./api";
import { toYmd } from "../../../../../../utils/dateOnly";

// Parse a stored fee to a number. Mastersheet fees may be plain numbers or
// currency-formatted strings ("$50.00", "1,050"); strip formatting before parsing
// so a string value like Telus's still prefills its real amount, not 0.
const num = (v) => {
  if (v === "" || v === null || v === undefined) return 0;
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  const cleaned = String(v).replace(/[$£€¥₹,\s]/g, "").trim();
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
};
const money = (v) => `$${num(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// The only fees an addendum may amend. field_key is the canonical MasterSheet
// key; `aliases` are alternate storage keys some property layouts use.
const FEE_FIELDS = [
  { field_key: "telus", label: "Telus Fee" },
  { field_key: "storageFee", label: "Storage Fee" },
  { field_key: "ssFee", label: "SS Fee", aliases: ["surfaceStallFee"] },
  { field_key: "ugFee", label: "UG Fee", aliases: ["undergroundFee"] },
  { field_key: "extraTenantFee", label: "Extra Tenant Fee" },
  { field_key: "dogFee", label: "Dog Fee" },
  { field_key: "catFee", label: "Cat Fee" },
];
// Fields that make up the monthly rent (drive the live total).
const MONTHLY_CHARGE_KEYS = ["basicRent", "ssFee", "ugFee", "dogFee", "catFee", "otherPetFee", "storageFee", "otherFee", "telus", "extraTenantFee"];

const normLabel = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
const hasValue = (v) => v !== undefined && v !== null && v !== "";

// Flatten a property's (possibly grouped) mastersheet field layout to {key,label}.
const flattenLayout = (fields) => {
  const out = [];
  const walk = (arr) =>
    (arr || []).forEach((f) => {
      if (f && f.type === "group" && Array.isArray(f.fields)) return walk(f.fields);
      if (f && f.key) out.push({ key: f.key, label: f.label || f.key });
    });
  walk(fields);
  return out;
};

// A property layout may store a fee under a custom key/label (e.g. a "Telus Fee"
// column keyed differently than `telus`). Resolve the key that actually holds
// this fee's value on the item so "Current" matches the mastersheet — and the
// write-back targets that same key.
const resolveFeeKey = (item, flatFields, fee) => {
  const wanted = normLabel(fee.label);
  const candidates = [fee.field_key, ...(fee.aliases || [])];
  flatFields.forEach((f) => {
    if (normLabel(f.label) === wanted && !candidates.includes(f.key)) candidates.push(f.key);
  });
  return candidates.find((k) => hasValue(item[k])) || fee.field_key;
};

// Map a stored key back to its whitelist option id (for the dropdown selection).
const canonicalPick = (key) => {
  const fee = FEE_FIELDS.find((f) => f.field_key === key || (f.aliases || []).includes(key));
  return fee ? fee.field_key : key || "";
};

const joinTenantNames = (item, tenants) => {
  // The mastersheet stores the tenant name under both `tenants` and `tenant`;
  // use whichever is a non-empty string before falling back to the tenants list.
  const fromItem =
    (typeof item.tenants === "string" && item.tenants.trim()) ||
    (typeof item.tenant === "string" && item.tenant.trim());
  if (fromItem) return fromItem;
  return (tenants || [])
    .map((t) => t.name || `${t.first_name || t.firstName || ""} ${t.last_name || t.lastName || ""}`.trim())
    .filter(Boolean)
    .join(", ");
};

const deriveAction = (oldVal, newVal) => {
  const o = num(oldVal);
  const n = num(newVal);
  if (n === 0 && o > 0) return "removed";
  if (!o && n > 0) return "added";
  return "updated";
};

// Normalize a stored amendment row (legacy rows may carry `change` instead of a total).
const normalizeRow = (r) => {
  if (r.type === "text") {
    return {
      pick: canonicalPick(r.field_key),
      field_key: r.field_key || "",
      label: r.label || "",
      display_name: r.display_name || "",
      type: "text",
      old_value: r.old_value ?? "",
      quantity: "",
      new_value: r.new_value ?? "",
    };
  }
  return {
    pick: canonicalPick(r.field_key),
    field_key: r.field_key || "",
    label: r.label || "",
    display_name: r.display_name || "",
    type: "number",
    old_value: num(r.old_value),
    quantity: r.quantity === undefined || r.quantity === null ? "" : r.quantity,
    new_value:
      r.new_value === undefined || r.new_value === null || r.new_value === ""
        ? r.change !== undefined
          ? num(r.old_value) + num(r.change)
          : ""
        : num(r.new_value),
  };
};

/* ── Design tokens ─────────────────────────────────────────────────────── */
const C = {
  primary: "#0C447C",
  primaryMd: "#1A6FB5",
  iconBg: "#EBF3FC",
  card: "#FFFFFF",
  border: "#E4E9F2",
  inputBorder: "#D6DEEA",
  muted: "#64748B",
  text: "#0F1F35",
  totalBg: "#EAF1FB",
};

const styles = {
  body: { padding: "26px 28px 16px", maxHeight: "76vh", overflowY: "auto" },
  label: { fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 8, display: "block" },
  smallLabel: { fontSize: 12.5, fontWeight: 600, color: C.muted, marginBottom: 6, display: "block" },
  fieldBox: {
    display: "flex", alignItems: "center", gap: 10, height: 48,
    border: `1px solid ${C.inputBorder}`, borderRadius: 10, padding: "0 14px", background: "#fff",
  },
  bareInput: {
    border: "none", outline: "none", background: "transparent", flex: 1, width: "100%",
    fontSize: 15, color: C.text, height: "100%", fontFamily: "inherit",
  },
  card: { border: `1px solid ${C.border}`, borderRadius: 12, padding: 18, background: "#fff" },
};

const IconBadge = ({ icon, size = 44, round = 12 }) => (
  <div style={{ width: size, height: size, borderRadius: round, background: C.iconBg, display: "flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto" }}>
    <FeatherIcon icon={icon} size={Math.round(size * 0.46)} style={{ color: C.primary }} />
  </div>
);

const Sparkle = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill={C.primaryMd} aria-hidden="true">
    <path d="M12 2.5l1.6 4.3 4.3 1.6-4.3 1.6L12 14.3l-1.6-4.3L6.1 8.4l4.3-1.6z" />
    <path d="M18.5 13.5l.8 2.1 2.1.8-2.1.8-.8 2.1-.8-2.1-2.1-.8 2.1-.8z" />
  </svg>
);

/**
 * Create / edit a Lease Addendum. The amendment section is a generic repeater:
 * pick a mastersheet field by its label, enter the change (numbers compute the
 * new value live), optionally give it a clearer tenant-facing name. The new
 * monthly total recomputes automatically.
 */
const AddendumModal = ({
  isOpen,
  toggle,
  itemId,
  item = {},
  tenants = [],
  fields = [],
  buildingName,
  propertyName = "",
  unitNo,
  defaults = {},
  existing,
  onSaved,
}) => {
  const propertyAddress = `${propertyName ? propertyName + " " : ""}${buildingName || ""}`.trim();
  const headerAddress = [unitNo, buildingName].filter(Boolean).join(" - ") || propertyAddress || "—";

  const flatFields = useMemo(() => flattenLayout(fields), [fields]);

  const prefill = useMemo(() => {
    if (existing) {
      return {
        tenant_names: existing.tenant_names || "",
        original_lease_date: toYmd(existing.original_lease_date),
        effective_date: toYmd(existing.effective_date),
        amendments: (Array.isArray(existing.amendments) ? existing.amendments : []).map(normalizeRow),
      };
    }
    return {
      tenant_names: "",
      original_lease_date: toYmd(item.leaseStartDmy),
      effective_date: "",
      amendments: [],
    };
  }, [existing, item]);

  // Whitelisted fees, plus any legacy field already on this draft (kept selectable
  // only for its own row).
  const pickList = useMemo(() => {
    const seen = new Set();
    const extras = (prefill.amendments || [])
      .filter((r) => (r.pick || r.field_key) && !FEE_FIELDS.some((f) => f.field_key === (r.pick || r.field_key)))
      .filter((r) => { const id = r.pick || r.field_key; return seen.has(id) ? false : seen.add(id); })
      .map((r) => ({ field_key: r.pick || r.field_key, label: r.label || r.field_key, legacy: true }));
    return [...FEE_FIELDS, ...extras];
  }, [prefill.amendments]);

  const [form, setForm] = useState(prefill);
  const [saving, setSaving] = useState(false);

  const amendments = form.amendments || [];

  const setRow = (i, patch) =>
    setForm((prev) => ({
      ...prev,
      amendments: prev.amendments.map((r, idx) => (idx === i ? { ...r, ...patch } : r)),
    }));

  const addAmendment = () =>
    setForm((prev) => ({
      ...prev,
      amendments: [
        ...(prev.amendments || []),
        { pick: "", field_key: "", label: "", display_name: "", type: "number", old_value: 0, quantity: "", new_value: "" },
      ],
    }));

  const removeAmendment = (i) =>
    setForm((prev) => ({ ...prev, amendments: prev.amendments.filter((_, idx) => idx !== i) }));

  const onPickField = (i, pickKey) => {
    const entry = pickList.find((p) => p.field_key === pickKey);
    if (!entry) {
      setRow(i, { pick: "", field_key: "", label: "", type: "number", old_value: 0, quantity: "", new_value: "" });
      return;
    }
    const storeKey = entry.legacy ? entry.field_key : resolveFeeKey(item, flatFields, entry);
    setRow(i, {
      pick: entry.field_key,
      field_key: storeKey,
      label: entry.label,
      type: "number",
      old_value: num(item[storeKey]),
      quantity: "",
      new_value: "",
    });
  };

  // Live new monthly total: current charges with amended values applied.
  const newTotal = useMemo(() => {
    const base = MONTHLY_CHARGE_KEYS.reduce((s, k) => {
      const fee = FEE_FIELDS.find((f) => f.field_key === k);
      const sk = fee ? resolveFeeKey(item, flatFields, fee) : k;
      return s + num(item[sk]);
    }, 0);
    let total = base;
    amendments.forEach((r) => {
      if (
        r.type !== "text" &&
        MONTHLY_CHARGE_KEYS.includes(r.pick || r.field_key) &&
        r.new_value !== "" && r.new_value !== null && r.new_value !== undefined
      ) {
        total += num(r.new_value) - num(r.old_value);
      }
    });
    return total;
  }, [amendments, item, flatFields]);

  const autofillTenants = () => {
    const names = joinTenantNames(item, tenants);
    if (!names) {
      toast.info("No tenants found on this unit to autofill");
      return;
    }
    setForm((prev) => ({ ...prev, tenant_names: names }));
  };

  const handleSave = async () => {
    if (!form.effective_date) {
      toast.error("Effective date is required");
      return;
    }
    const cleaned = amendments.filter((r) => r.field_key);
    if (!cleaned.length) {
      toast.error("Add at least one amendment");
      return;
    }
    const missingCharge = cleaned.find(
      (r) => r.type !== "text" && (r.new_value === "" || r.new_value === null || r.new_value === undefined)
    );
    if (missingCharge) {
      toast.error(`Enter the total charge for ${missingCharge.display_name || missingCharge.label}`);
      return;
    }
    const negative = cleaned.find((r) => r.type !== "text" && num(r.new_value) < 0);
    if (negative) {
      toast.error(`Total charge for ${negative.display_name || negative.label} can't be negative`);
      return;
    }
    setSaving(true);
    try {
      const tenantSignatures = (tenants || []).length
        ? tenants.map((t, idx) => ({
            tenant_index: idx,
            name: t.name || `${t.first_name || t.firstName || ""} ${t.last_name || t.lastName || ""}`.trim(),
            email: t.email || "",
            signed: false,
          }))
        : undefined;

      const outAmendments = cleaned.map((r) => {
        if (r.type === "text") {
          return {
            field_key: r.field_key,
            label: r.label,
            display_name: (r.display_name || "").trim(),
            type: "text",
            action: "updated",
            old_value: r.old_value,
            new_value: r.new_value,
          };
        }
        return {
          field_key: r.field_key,
          label: r.label,
          display_name: (r.display_name || "").trim(),
          type: "number",
          action: deriveAction(r.old_value, r.new_value),
          old_value: num(r.old_value),
          ...(r.quantity === "" || r.quantity === null || r.quantity === undefined
            ? {}
            : { quantity: num(r.quantity) }),
          new_value: num(r.new_value),
        };
      });

      const payload = {
        mastersheet_item_id: itemId,
        property_id: defaults.property_id,
        building_name: buildingName,
        unit_no: unitNo,
        property_address: propertyAddress,
        tenant_names: form.tenant_names,
        original_lease_date: form.original_lease_date || undefined,
        effective_date: form.effective_date,
        amendments: outAmendments,
        revised_rent: { new_total_monthly_rent: newTotal },
        ...(tenantSignatures ? { tenant_signatures: tenantSignatures } : {}),
      };
      if (existing?._id) {
        await updateAddendum(existing._id, payload);
        toast.success("Addendum updated");
      } else {
        await createAddendum(payload);
        toast.success("Addendum created");
      }
      onSaved && onSaved();
      toggle();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to save addendum");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered contentClassName="addendum-modal-content">
      <style>{`
        .addendum-modal-content { border: none; border-radius: 16px; overflow: hidden; box-shadow: 0 24px 70px rgba(12,68,124,0.20); }
        .addendum-modal-content input::placeholder { color: #9AA7B8; }
        .am-link { background: none; border: none; padding: 0; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; font-size: 14px; font-weight: 600; color: ${C.primaryMd}; }
        .am-link:hover { color: ${C.primary}; }
        .am-add { width: 100%; display: flex; align-items: center; justify-content: center; gap: 8px; padding: 13px; margin-top: 14px;
          border: 1.5px dashed ${C.inputBorder}; border-radius: 10px; background: #fff; cursor: pointer;
          font-size: 14px; font-weight: 600; color: ${C.primary}; transition: background .15s, border-color .15s; }
        .am-add:hover { background: ${C.iconBg}; border-color: ${C.primaryMd}; }
        .am-btn { border: none; cursor: pointer; font-size: 14.5px; font-weight: 600; border-radius: 10px; padding: 11px 22px; display: inline-flex; align-items: center; gap: 8px; }
        .am-btn-primary { background: ${C.primary}; color: #fff; box-shadow: 0 6px 18px rgba(12,68,124,0.28); }
        .am-btn-primary:hover:not(:disabled) { background: #083460; }
        .am-btn-primary:disabled { background: #94A3B8; box-shadow: none; cursor: not-allowed; }
        .am-btn-ghost { background: #fff; color: ${C.text}; border: 1px solid ${C.inputBorder}; }
        .am-btn-ghost:hover { background: #F1F5FA; }
        .am-iconbtn { background: none; border: none; cursor: pointer; color: ${C.muted}; display: inline-flex; }
        .am-iconbtn:hover { color: #991B1B; }
      `}</style>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 16, padding: "24px 28px 0" }}>
        <IconBadge icon="file-text" />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: C.text, lineHeight: 1.2 }}>
            {existing ? "Edit lease addendum" : "Create lease addendum"}
          </div>
          <div style={{ fontSize: 14, color: C.muted, marginTop: 4 }}>
            Add an amendment to this lease and apply the changes to the master lease.
          </div>
        </div>
        <button className="am-iconbtn" style={{ color: C.muted, marginTop: 2 }} onClick={toggle} title="Close">
          <FeatherIcon icon="x" size={24} />
        </button>
      </div>

      {/* Body */}
      <div style={styles.body}>
        {/* Property card */}
        <div style={{ ...styles.card, display: "flex", alignItems: "center", gap: 16, padding: "16px 20px", marginBottom: 22 }}>
          <div style={{ width: 48, height: 48, borderRadius: "50%", background: C.iconBg, display: "flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto" }}>
            <FeatherIcon icon="home" size={22} style={{ color: C.primary }} />
          </div>
          <div>
            <div style={{ fontSize: 13, color: C.muted, fontWeight: 600 }}>Property</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: C.text }}>{headerAddress}</div>
          </div>
        </div>

        {/* Tenants */}
        <div style={{ marginBottom: 22 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <span style={{ ...styles.label, marginBottom: 0 }}>Tenant(s)</span>
            <button className="am-link" onClick={autofillTenants}>
              <Sparkle /> Autofill from unit
            </button>
          </div>
          <div style={styles.fieldBox}>
            <FeatherIcon icon="user" size={18} style={{ color: C.muted, flex: "0 0 auto" }} />
            <input
              style={styles.bareInput}
              placeholder="Enter tenant name(s), or click Autofill"
              value={form.tenant_names || ""}
              onChange={(e) => setForm((p) => ({ ...p, tenant_names: e.target.value }))}
            />
          </div>
        </div>

        {/* Dates */}
        <div style={{ display: "flex", gap: 18, marginBottom: 22, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 240px" }}>
            <span style={styles.label}>Original lease date</span>
            <div style={styles.fieldBox}>
              <FeatherIcon icon="calendar" size={18} style={{ color: C.muted, flex: "0 0 auto" }} />
              <input type="date" style={styles.bareInput} value={toYmd(form.original_lease_date)} onChange={(e) => setForm((p) => ({ ...p, original_lease_date: e.target.value }))} />
            </div>
          </div>
          <div style={{ flex: "1 1 240px" }}>
            <span style={styles.label}>Effective date of amendment</span>
            <div style={styles.fieldBox}>
              <FeatherIcon icon="calendar" size={18} style={{ color: C.muted, flex: "0 0 auto" }} />
              <input type="date" style={styles.bareInput} value={toYmd(form.effective_date)} onChange={(e) => setForm((p) => ({ ...p, effective_date: e.target.value }))} />
            </div>
          </div>
        </div>

        {/* Amendment details */}
        <div style={styles.card}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
            <IconBadge icon="edit-2" size={40} round={10} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>Amendment details</div>
              {amendments.length === 0 && (
                <div style={{ fontSize: 13.5, color: C.muted, marginTop: 3 }}>
                  No amendments yet. Add the fields you want this addendum to change.
                </div>
              )}
            </div>
          </div>

          {amendments.map((row, i) => {
            return (
              <div key={i} style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 14, marginTop: 14, background: "#FBFCFE" }}>
                <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
                  <div style={{ flex: "1 1 200px" }}>
                    <span style={styles.smallLabel}>Field to amend</span>
                    <select
                      style={{ width: "100%", height: 44, border: `1px solid ${C.inputBorder}`, borderRadius: 10, padding: "0 12px", background: "#fff", fontSize: 15, color: C.text, outline: "none", cursor: "pointer", fontFamily: "inherit" }}
                      value={row.pick || ""}
                      onChange={(e) => onPickField(i, e.target.value)}
                    >
                      <option value="">Select a field…</option>
                      {pickList.map((p) => (
                        <option key={p.field_key} value={p.field_key} disabled={!!p.legacy && (row.pick || row.field_key) !== p.field_key}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={{ flex: "1 1 220px" }}>
                    <span style={styles.smallLabel}>Name shown to tenant (optional)</span>
                    <div style={{ ...styles.fieldBox, height: 44 }}>
                      <input
                        style={styles.bareInput}
                        placeholder={row.label || "e.g. Surface stall fee"}
                        value={row.display_name || ""}
                        onChange={(e) => setRow(i, { display_name: e.target.value })}
                      />
                    </div>
                  </div>
                  <button className="am-iconbtn" style={{ height: 44 }} title="Remove" onClick={() => removeAmendment(i)}>
                    <FeatherIcon icon="trash-2" size={18} />
                  </button>
                </div>

                {row.field_key && row.type === "text" && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                      <div style={{ flex: "1 1 160px" }}>
                        <span style={styles.smallLabel}>Current</span>
                        <div style={{ ...styles.fieldBox, height: 44, background: "#F1F5FA", color: C.muted }}>{row.old_value === "" ? "—" : String(row.old_value)}</div>
                      </div>
                      <div style={{ flex: "1 1 220px" }}>
                        <span style={styles.smallLabel}>New value</span>
                        <div style={{ ...styles.fieldBox, height: 44, background: "#F1F5FA", color: C.muted }}>{row.new_value === "" ? "—" : String(row.new_value)}</div>
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>Legacy text amendment — kept as saved; it can be removed but not edited here.</div>
                  </div>
                )}
                {row.field_key && row.type !== "text" && (
                  <div style={{ display: "flex", gap: 12, marginTop: 12, flexWrap: "wrap" }}>
                    <div style={{ flex: "1 1 120px" }}>
                      <span style={styles.smallLabel}>Current</span>
                      <div style={{ ...styles.fieldBox, height: 44, background: "#F1F5FA", color: C.muted }}>{money(row.old_value)}</div>
                    </div>
                    <div style={{ flex: "1 1 120px" }}>
                      <span style={styles.smallLabel}>Quantity (optional)</span>
                      <div style={{ ...styles.fieldBox, height: 44 }}>
                        <input type="number" min="0" style={styles.bareInput} placeholder="e.g. 2" value={row.quantity} onChange={(e) => setRow(i, { quantity: e.target.value })} />
                      </div>
                    </div>
                    <div style={{ flex: "1 1 150px" }}>
                      <span style={styles.smallLabel}>Total charge / month</span>
                      <div style={{ ...styles.fieldBox, height: 44 }}>
                        <input type="number" min="0" style={styles.bareInput} placeholder="Exact amount" value={row.new_value} onChange={(e) => setRow(i, { new_value: e.target.value })} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          <button className="am-add" onClick={addAmendment}>
            <FeatherIcon icon="plus" size={18} /> Add amendment
          </button>
        </div>

        {/* New total */}
        <div style={{ display: "flex", alignItems: "center", gap: 16, background: C.totalBg, borderRadius: 12, padding: "16px 20px", marginTop: 18 }}>
          <div style={{ width: 46, height: 46, borderRadius: "50%", background: C.primary, display: "flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto" }}>
            <FeatherIcon icon="dollar-sign" size={22} style={{ color: "#fff" }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.primary }}>New total monthly rent payable</div>
            <div style={{ fontSize: 13, color: C.muted, marginTop: 2 }}>This reflects the updated amount after applying this addendum.</div>
          </div>
          <div style={{ fontSize: 28, fontWeight: 800, color: C.primary, whiteSpace: "nowrap" }}>{money(newTotal)}</div>
        </div>

        {/* Footer note */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginTop: 16 }}>
          <FeatherIcon icon="shield" size={17} style={{ color: "#16A34A", flex: "0 0 auto", marginTop: 1 }} />
          <span style={{ fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
            All listed tenants must e-sign, then a manager marks the addendum completed to apply the changes to the mastersheet.
          </span>
        </div>
      </div>

      {/* Footer actions */}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, padding: "16px 28px 24px", borderTop: `1px solid ${C.border}` }}>
        <button className="am-btn am-btn-ghost" onClick={toggle} disabled={saving}>Cancel</button>
        <button className="am-btn am-btn-primary" onClick={handleSave} disabled={saving}>
          <FeatherIcon icon="save" size={17} />
          {saving ? "Saving…" : existing ? "Save changes" : "Save draft"}
        </button>
      </div>
    </Modal>
  );
};

export default AddendumModal;
